import { z } from 'zod';
import { activityStatusSchema } from './activity.js';
import { optionalText, requiredText } from './field.js';
/**
 * Invoices (rules 8, 9, 10 and 11 in `.claude/rules/billing.md`).
 *
 * Two things shape this file. First, everything on a finalized invoice is a
 * **snapshot**: services, texts and the contact's address all stay editable,
 * and the document has to read identically for the whole retention period.
 * Second, a finalized invoice is immutable — the trigger in migration 0014
 * enforces it, and payments live in their own table so they never touch the
 * row.
 */
export const invoiceTypes = ['invoice', 'cancellation_invoice'];
export const invoiceTypeSchema = z.enum(invoiceTypes);
export const invoiceStatuses = ['draft', 'finalized', 'cancelled'];
export const invoiceStatusSchema = z.enum(invoiceStatuses);
/**
 * The recipient as they were when the invoice was finalized.
 *
 * `name` is produced by `formatContactName`, the same function the screen
 * uses, so the stored name reads exactly like the one that was checked before
 * finalizing.
 *
 * **Every field here is optional with a default, and that is a property of a
 * snapshot rather than a concession to old rows.** What is stored is what the
 * contact looked like at the moment of finalizing. When the contact schema
 * grows a field afterwards — `houseNumber` did — every snapshot written before
 * that simply does not have the key, and reading it must produce the same
 * document it produced on the day it was issued. This holds after going live
 * as much as before it.
 *
 * Exported so `contact-address.test.ts` can parse an old snapshot directly and
 * hold that rule in place; `invoiceSchema` below is where it is used.
 */
export const recipientSnapshotSchema = z.object({
    contactNumber: z.number().int(),
    name: z.string(),
    /**
     * The salutation as **text**, not a reference: a later rename must not
     * change a document that was printed long ago. Added in D-R3, hence
     * optional with a default like every field after the first version.
     *
     * Nothing renders it yet — the PDF's address block has no salutation line.
     * It is stored because a snapshot holds what was true, not only what was
     * printed; whether the line arrives is a decision for the invoice screen.
     */
    salutation: z.string().nullable().default(null),
    contactPerson: z.string().nullable().default(null),
    street: z.string().nullable().default(null),
    houseNumber: z.string().nullable().default(null),
    postalCode: z.string().nullable().default(null),
    city: z.string().nullable().default(null),
    /**
     * Nullable since D-R3, when the contact's country became genuinely optional
     * — but **still a required key**, with no default. A snapshot that never
     * carried the field at all is a different thing from one that recorded "no
     * country", and only the second may be written from now on.
     */
    country: z.string().nullable(),
    vatId: z.string().nullable().default(null),
});
/** A line as it is submitted while the invoice is still a draft. */
export const invoiceLineInputSchema = z.object({
    id: z.uuid().optional(),
    /** Where the line came from. Null for a free line typed by hand. */
    activityItemId: z.uuid().nullable().default(null),
    description: requiredText(200),
    feeCode: optionalText(40),
    dateOfService: z.iso.date().nullable().default(null),
    quantity: z.number().int().positive().max(999).default(1),
    /** No sign restriction, like `activity_item.unit_price_cents`. */
    unitPriceCents: z.number().int().min(-100_000_000).max(100_000_000),
});
export const invoiceLineSchema = z.object({
    id: z.uuid(),
    position: z.number().int(),
    activityItemId: z.uuid().nullable(),
    /**
     * The activity that item belongs to — derived on read, stored nowhere, and
     * null on a free line typed by hand (K7). It exists so a list can say how
     * many *activities* an invoice covers: several lines routinely come out of
     * one session, and counting lines answers a different question.
     */
    activityId: z.uuid().nullable(),
    /**
     * The three things a screen needs to *name* that Vorgang, joined in beside
     * it and stored nowhere (L8): when it happened, what kind it was and what it
     * was called. The draft groups its positions by Vorgang, and a group whose
     * header can only say a date is a box with a date on it.
     *
     * They travel here rather than being fetched separately because the join
     * that yields `activityId` already reaches the row — three columns on a join
     * that exists, against one request per invoice.
     */
    activityOccurredAt: z.iso.datetime().nullable(),
    activityTypeId: z.uuid().nullable(),
    activityTitle: z.string().nullable(),
    description: z.string(),
    feeCode: z.string().nullable(),
    dateOfService: z.iso.date().nullable(),
    quantity: z.number().int(),
    unitPriceCents: z.number().int(),
    amountCents: z.number().int(),
});
/** Creating a draft, optionally filled from a contact's billable items. */
export const invoiceCreateSchema = z.object({
    contactId: z.uuid(),
    invoiceDate: z.iso.date(),
    /** Left out: taken from `practice_settings.default_payment_term_days`. */
    paymentTermDays: z.number().int().min(0).max(365).optional(),
    activityItemIds: z.array(z.uuid()).max(200).default([]),
});
/**
 * Editing a draft. The texts are plain text and not a reference to a template:
 * picking a template fills them, and they stay adjustable for this one
 * invoice. A foreign key to a mutable table on a row that becomes immutable
 * would need `ON DELETE SET NULL`, and that is an UPDATE the trigger would
 * refuse.
 */
export const invoiceUpdateSchema = z.object({
    invoiceDate: z.iso.date(),
    paymentTermDays: z.number().int().min(0).max(365),
    /**
     * Who the invoice is addressed to (L8). **Null means the contact itself**,
     * which is what every invoice meant before this existed — a bare id would
     * have made "the usual case" indistinguishable from "somebody happened to
     * pick the patient", and the two are not the same statement.
     *
     * The server accepts only a contact the patient actually has a
     * `billing_recipient` relation to; a free choice would let an invoice be
     * addressed to anyone in the card index by editing one request.
     */
    recipientContactId: z.uuid().nullable().default(null),
    introText: optionalText(4000),
    outroText: optionalText(4000),
    /** Prefilled from `contact.diagnosis` when the draft is created, then free
     *  to edit — same reasoning as the intro and outro texts (rule 12 in
     *  `.claude/rules/architecture.md`: appears on the draft and the PDF, never in a log or an error). */
    diagnosis: optionalText(4000),
    lines: z.array(invoiceLineInputSchema).max(200).default([]),
});
export const invoiceSchema = z.object({
    id: z.uuid(),
    contactId: z.uuid(),
    contactName: z.string(),
    contactNumber: z.number().int(),
    /** Null while the invoice goes to the contact itself — see the update
     *  schema. The *name* travels beside it, joined on read like `contactName`,
     *  so a row can say who it is addressed to without a second request. */
    recipientContactId: z.uuid().nullable(),
    recipientName: z.string().nullable(),
    type: invoiceTypeSchema,
    status: invoiceStatusSchema,
    number: z.string().nullable(),
    numberPrefix: z.string().nullable(),
    numberValue: z.number().int().nullable(),
    invoiceDate: z.iso.date(),
    paymentTermDays: z.number().int(),
    recipientSnapshot: recipientSnapshotSchema.nullable(),
    introText: z.string().nullable(),
    outroText: z.string().nullable(),
    diagnosis: z.string().nullable(),
    totalCents: z.number().int(),
    /**
     * The sum of this invoice's payments (slice 8). Derived, never stored — it
     * is read back with the invoice so a list can show the payment state without
     * a second round trip. What that sum *means* is `invoicePaymentState()` in
     * `payment.ts`, which is the only place the status is decided.
     */
    paidCents: z.number().int(),
    /**
     * The day the newest of those payments arrived (K7), so a row can date
     * "bezahlt" instead of only asserting it. Derived exactly like `paidCents`
     * one line up, and for the same reason there is no column: a second place
     * saying when the money came in would eventually say something else.
     */
    lastPaidOn: z.iso.date().nullable(),
    /**
     * The last *successful* send (slice 10). Derived from `invoice_send`, never
     * stored — the same reasoning as `paidCents` one line up, and the reason
     * there are no `sent_at` / `sent_to` columns: the log already knows, and a
     * second place saying when it went out would eventually say something else.
     *
     * It also keeps the slice additive. Columns here would have meant widening
     * the allowlist of `protect_finalized_invoice` (migration 0019), which is
     * the immutability of a finalized document.
     */
    lastSentAt: z.iso.datetime().nullable(),
    lastSentTo: z.string().nullable(),
    pdfHash: z.string().nullable(),
    finalizedAt: z.iso.datetime().nullable(),
    /**
     * The two ends of a cancellation, both stored (rule 9 in `.claude/rules/billing.md`): the
     * cancellation document points at the invoice it takes back, the original
     * points at the document that took it back. The numbers travel with them so
     * a list can print the link without looking the other row up — with a status
     * filter the counterpart may not even be in the response.
     */
    cancelsInvoiceId: z.uuid().nullable(),
    cancelsInvoiceNumber: z.string().nullable(),
    cancelledByInvoiceId: z.uuid().nullable(),
    cancelledByInvoiceNumber: z.string().nullable(),
    lines: z.array(invoiceLineSchema),
});
export const invoiceListQuerySchema = z.object({
    contactId: z.uuid().optional(),
    status: invoiceStatusSchema.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(100),
    offset: z.coerce.number().int().min(0).default(0),
});
/** An activity item that may still be put on an invoice. */
export const billableItemSchema = z.object({
    id: z.uuid(),
    activityId: z.uuid(),
    /** Carried because the list may span contacts; the draft picker on a single
     *  contact ignores it. */
    contactId: z.uuid(),
    contactNumber: z.number().int(),
    contactName: z.string(),
    occurredAt: z.iso.datetime(),
    activityTitle: z.string().nullable(),
    /** The id of the activity's type. Sent so the picker can fall back to its
     *  label where the activity has no title of its own — the client resolves it
     *  from the catalogue, like everywhere else. */
    activityTypeId: z.uuid(),
    /**
     * What became of the treatment. Shown, never filtered on: a past activity
     * still standing on "geplant" is the one worth noticing, and a filter would
     * hide it. There is no status parameter in the query below, which is what
     * makes that a property of the API rather than a habit.
     */
    activityStatus: activityStatusSchema,
    description: z.string(),
    feeCode: z.string().nullable(),
    quantity: z.number().int(),
    unitPriceCents: z.number().int(),
});
/**
 * `contactId` narrows the list to one contact; without it the list spans all
 * of them.
 *
 * There is deliberately **no status field here.** Billability does not depend
 * on a status (rule 6), so a filter over it could only ever hide work that is
 * still owed — and what cannot be expressed cannot be added by habit either.
 */
export const billableQuerySchema = z.object({ contactId: z.uuid().optional() });
/**
 * Turn billable items into drafts: one per contact, appending to the draft a
 * contact already has rather than opening a second one.
 *
 * Both ways into billing use this — the button on a single activity and the
 * bulk action on the billable list. They differ only in how many items they
 * hand over, so they are one operation and not two.
 */
export const invoiceCollectSchema = z.object({
    activityItemIds: z.array(z.uuid()).min(1).max(500),
    invoiceDate: z.iso.date(),
});
/** What became of one contact's share of a `collect`. */
export const invoiceCollectResultSchema = z.object({
    invoiceId: z.uuid(),
    contactId: z.uuid(),
    contactName: z.string(),
    /** False when the items were appended to a draft that already existed. */
    created: z.boolean(),
    addedLines: z.number().int(),
});
/** Sum of an invoice's lines. Used by the draft editor and by finalization, so
 *  the number on screen and the number on the document come from one place. */
export function sumLines(lines) {
    return lines.reduce((total, line) => total + line.quantity * line.unitPriceCents, 0);
}
/** Invoice date plus payment term. Derived, never stored. */
export function dueDate(invoiceDate, paymentTermDays) {
    const due = new Date(`${invoiceDate}T12:00:00Z`);
    due.setUTCDate(due.getUTCDate() + paymentTermDays);
    return due.toISOString().slice(0, 10);
}
//# sourceMappingURL=invoice.js.map